import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { FOUNDATION, ARTICLE_CATEGORIES, BOOK_CATEGORIES, RESEARCH_CATEGORIES, GALLERY_CATEGORIES } from "@/lib/api";
import { ResourceManager, RowThumb } from "@/components/admin/ResourceManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LogOut, FileText, BookOpen, Microscope, Newspaper, Image, Lightbulb, Mail, Home as HomeIcon, Info, Loader2, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ICON_OPTIONS = ["landmark", "compass", "microscope", "camera", "book"];

const TABS = [
  { key: "articles", label: "लेख", icon: FileText },
  { key: "books", label: "पुस्तकें", icon: BookOpen },
  { key: "research", label: "शोध एवं प्रकाशन", icon: Microscope },
  { key: "magazines", label: "टूरिस्ट संदेश मासिक", icon: Newspaper },
  { key: "gallery", label: "फोटो गैलरी", icon: Image },
  { key: "initiatives", label: "हमारी पहल", icon: Lightbulb },
  { key: "about", label: "परिचय पृष्ठ", icon: Info },
  { key: "messages", label: "संदेश", icon: Mail },
];

const CONFIGS = {
  articles: {
    title: "लेख", endpoint: "articles",
    empty: { title: "", category: "अन्य", author: "", date: "", summary: "", content: "", image_url: "" },
    fields: [
      { key: "title", label: "शीर्षक", required: true },
      { key: "category", label: "श्रेणी", type: "select", options: ARTICLE_CATEGORIES },
      { key: "author", label: "लेखक" },
      { key: "date", label: "प्रकाशन तिथि (जैसे: १५ जून २०२६)" },
      { key: "image_url", label: "मुख्य छवि (Featured Image)", type: "image" },
      { key: "summary", label: "संक्षिप्त विवरण", type: "textarea", rows: 2 },
      { key: "content", label: "पूरा लेख", type: "textarea", rows: 8 },
    ],
    row: (it) => <div className="flex items-center gap-3"><RowThumb url={it.image_url} fallback="feather-pointed" /><div className="min-w-0"><p className="font-medium text-[#0a2540] truncate">{it.title}</p><p className="text-xs text-slate-500">{it.category} • {it.author}</p></div></div>,
  },
  books: {
    title: "पुस्तक", endpoint: "books",
    empty: { title: "", author: "", category: "अन्य", publication_info: "", description: "", cover_url: "", pdf_url: "" },
    fields: [
      { key: "title", label: "पुस्तक शीर्षक", required: true },
      { key: "author", label: "लेखक" },
      { key: "category", label: "श्रेणी", type: "select", options: BOOK_CATEGORIES },
      { key: "publication_info", label: "प्रकाशन जानकारी (वर्ष, प्रकाशक)" },
      { key: "cover_url", label: "पुस्तक कवर", type: "image" },
      { key: "pdf_url", label: "पुस्तक PDF", type: "pdf" },
      { key: "description", label: "संक्षिप्त विवरण", type: "textarea" },
    ],
    row: (it) => <div className="flex items-center gap-3"><RowThumb url={it.cover_url} fallback="book" /><div className="min-w-0"><p className="font-medium text-[#0a2540] truncate">{it.title}</p><p className="text-xs text-slate-500">{it.author} • {it.category}</p></div></div>,
  },
  research: {
    title: "शोध/प्रकाशन", endpoint: "research",
    empty: { title: "", author: "", year: "", category: "शोध पत्र", description: "", pdf_url: "" },
    fields: [
      { key: "title", label: "शीर्षक", required: true },
      { key: "author", label: "लेखक" },
      { key: "year", label: "वर्ष" },
      { key: "category", label: "प्रकार", type: "select", options: RESEARCH_CATEGORIES },
      { key: "pdf_url", label: "PDF", type: "pdf" },
      { key: "description", label: "संक्षिप्त विवरण", type: "textarea" },
    ],
    row: (it) => <div className="flex items-center gap-3"><RowThumb url={it.pdf_url} kind="pdf" /><div className="min-w-0"><p className="font-medium text-[#0a2540] truncate">{it.title}</p><p className="text-xs text-slate-500">{it.category} • {it.year}</p></div></div>,
  },
  magazines: {
    title: "मासिक अंक", endpoint: "magazines",
    empty: { title: "", issue_number: "", year: "", month: "", date: "", description: "", cover_url: "", pdf_url: "" },
    fields: [
      { key: "title", label: "अंक शीर्षक", required: true },
      { key: "issue_number", label: "अंक संख्या" },
      { key: "year", label: "वर्ष (जैसे: 2026)" },
      { key: "month", label: "माह (जैसे: जून)" },
      { key: "date", label: "प्रकाशन तिथि" },
      { key: "cover_url", label: "पत्रिका कवर", type: "image" },
      { key: "pdf_url", label: "पत्रिका PDF", type: "pdf" },
      { key: "description", label: "संक्षिप्त विवरण", type: "textarea" },
    ],
    row: (it) => <div className="flex items-center gap-3"><RowThumb url={it.cover_url} fallback="newspaper" /><div className="min-w-0"><p className="font-medium text-[#0a2540] truncate">{it.title}</p><p className="text-xs text-slate-500">{it.month} {it.year} • अंक {it.issue_number}</p></div></div>,
  },
  gallery: {
    title: "तस्वीर", endpoint: "gallery",
    empty: { title: "", category: "अन्य गतिविधियाँ", image_url: "" },
    fields: [
      { key: "image_url", label: "तस्वीर", type: "image" },
      { key: "title", label: "शीर्षक / विवरण" },
      { key: "category", label: "श्रेणी", type: "select", options: GALLERY_CATEGORIES },
    ],
    row: (it) => <div className="flex items-center gap-3"><RowThumb url={it.image_url} /><div className="min-w-0"><p className="font-medium text-[#0a2540] truncate">{it.title || "(बिना शीर्षक)"}</p><p className="text-xs text-slate-500">{it.category}</p></div></div>,
  },
  initiatives: {
    title: "पहल", endpoint: "initiatives",
    empty: { title: "", description: "", icon: "landmark", image_url: "", order: 0 },
    fields: [
      { key: "title", label: "शीर्षक", required: true },
      { key: "icon", label: "आइकन", type: "select", options: ICON_OPTIONS },
      { key: "order", label: "क्रम (Order)", type: "number" },
      { key: "image_url", label: "छवि (वैकल्पिक)", type: "image" },
      { key: "description", label: "विवरण", type: "textarea", rows: 5 },
    ],
    row: (it) => <div className="flex items-center gap-3"><RowThumb url={it.image_url} fallback="lightbulb" /><div className="min-w-0"><p className="font-medium text-[#0a2540] truncate">{it.title}</p><p className="text-xs text-slate-500 truncate">{it.description}</p></div></div>,
  },
};

const AboutEditor = () => {
  const [content, setContent] = useState({ intro: "", history: "", vision: "", mission: "", objectives: [] });
  const [objText, setObjText] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/pages/about").then((res) => {
      const c = res.data.content || {};
      setContent({ intro: c.intro || "", history: c.history || "", vision: c.vision || "", mission: c.mission || "", objectives: c.objectives || [] });
      setObjText((c.objectives || []).join("\n"));
    }).finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const objectives = objText.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/pages/about", { key: "about", content: { ...content, objectives } });
      toast.success("परिचय पृष्ठ सहेजा गया");
    } catch { toast.error("सहेजने में त्रुटि"); }
    setSaving(false);
  };

  if (!loaded) return <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl text-[#0a2540] mb-2">परिचय पृष्ठ संपादित करें</h2>
      <p className="text-sm text-slate-500 mb-6">खाली छोड़ने पर डिफ़ॉल्ट पाठ दिखाया जाएगा।</p>
      <div className="space-y-4">
        {[
          { k: "intro", l: "परिचय (Introduction)", rows: 4 },
          { k: "history", l: "इतिहास (History)", rows: 4 },
          { k: "vision", l: "दृष्टि (Vision)", rows: 2 },
          { k: "mission", l: "ध्येय (Mission)", rows: 2 },
        ].map((f) => (
          <div key={f.k}>
            <Label className="text-[#0a2540]">{f.l}</Label>
            <Textarea value={content[f.k]} onChange={(e) => setContent({ ...content, [f.k]: e.target.value })} rows={f.rows} className="mt-1.5 border-amber-200" data-testid={`about-${f.k}`} />
          </div>
        ))}
        <div>
          <Label className="text-[#0a2540]">उद्देश्य (प्रत्येक पंक्ति में एक)</Label>
          <Textarea value={objText} onChange={(e) => setObjText(e.target.value)} rows={6} className="mt-1.5 border-amber-200" data-testid="about-objectives" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-about-button" className="bg-[#0a2540] hover:bg-[#061729] gap-2">
          {saving && <Loader2 size={16} className="animate-spin" />} सहेजें
        </Button>
      </div>
    </div>
  );
};

const Messages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const load = () => api.get("/contact").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const del = async () => { await api.delete(`/contact/${deleteId}`); toast.success("हटा दिया"); setDeleteId(null); load(); };

  if (loading) return <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;
  return (
    <div>
      <h2 className="font-display text-2xl text-[#0a2540] mb-6">संपर्क संदेश ({items.length})</h2>
      {items.length === 0 ? <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-amber-200/60">कोई संदेश नहीं।</div> : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-amber-200/60 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-[#0a2540]">{m.name} {m.subject && <span className="text-sm text-amber-700">— {m.subject}</span>}</p>
                  <p className="text-xs text-slate-500 font-eng mt-0.5">{m.email} {m.phone && `• ${m.phone}`}</p>
                  <p className="text-slate-600 mt-2 whitespace-pre-wrap">{m.message}</p>
                  <p className="text-xs text-slate-400 mt-2 font-eng">{new Date(m.created_at).toLocaleString("hi-IN")}</p>
                </div>
                <Button size="icon" variant="outline" onClick={() => setDeleteId(m.id)} data-testid={`delete-message-${m.id}`} className="border-red-300 text-red-600 hover:bg-red-50 shrink-0"><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>क्या आप निश्चित हैं?</AlertDialogTitle>
            <AlertDialogDescription>यह संदेश स्थायी रूप से हटा दिया जाएगा।</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>रद्द करें</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete-message-button" className="bg-red-600 hover:bg-red-700">हटाएं</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [admin, loading, navigate]);

  if (loading || !admin) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-amber-600" size={32} /></div>;

  const cfg = CONFIGS[tab];

  return (
    <div className="min-h-screen bg-ivory flex flex-col lg:flex-row" style={{ background: "#faf9f5" }}>
      {/* Sidebar */}
      <aside className="lg:w-72 bg-[#061729] text-white lg:min-h-screen shrink-0">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src={FOUNDATION.logo} alt="logo" className="w-11 h-11 rounded-full object-cover ring-2 ring-amber-400/60" />
          <div>
            <p className="font-display text-lg leading-tight">प्रबंधन पैनल</p>
            <p className="text-[10px] text-amber-400 font-eng">Admin Dashboard</p>
          </div>
        </div>
        <nav className="p-3 flex flex-row lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.key ? "bg-amber-500 text-white" : "text-slate-300 hover:bg-white/10"
              }`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 lg:mt-auto space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-slate-300 hover:bg-white/10">
            <HomeIcon size={18} /> वेबसाइट देखें
          </button>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-button" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-red-300 hover:bg-red-500/20">
            <LogOut size={18} /> लॉगआउट
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 p-5 sm:p-8 max-w-5xl">
        {tab === "about" ? <AboutEditor /> : tab === "messages" ? <Messages /> : (
          <ResourceManager endpoint={cfg.endpoint} title={cfg.title} fields={cfg.fields} emptyItem={cfg.empty} renderRow={cfg.row} />
        )}
      </div>
    </div>
  );
}
